import {
    ComboBox, Modal, Tooltip,
} from '@carbon/react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './UserAppRoleSelect.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import { UserRole } from 'api/dtos/user';
import useGetUserPermission from 'hooks/useGetUserPermission';
import useGetUser from 'hooks/useGetUser';

type EntityAppRole = {
    id: string;
    role: UserRole;
    title: string;
}

const roles: EntityAppRole[] = [
    {
        id: '0',
        role: UserRole.Owner,
        title: 'Właściciel',
    },
    {
        id: '1',
        role: UserRole.Admin,
        title: 'Admin',
    },
    {
        id: '2',
        role: UserRole.Moderator,
        title: 'Moderator',
    },
    {
        id: '3',
        role: UserRole.TeamMember,
        title: 'Użytkownik',
    },
    {
        id: '4',
        role: UserRole.Guest,
        title: 'Gość',
    },
];

const selectableRoles = roles.filter((appRole) => appRole.role !== UserRole.Owner);

type Props = {
    id: string;
    role: UserRole;
    isLoading: boolean;
    onSelectRole: (updatedRole: UserRole, callback?: () => void) => void;
    isError?: boolean;
}

const UserAppRoleSelect = ({
    id,
    role,
    onSelectRole,
    isLoading = false,
    isError,
}: Props) => {
    const intl = useIntl();
    const user = useGetUser();
    const { isAdmin, isOwner } = useGetUserPermission();
    const canManageRoles = isOwner || isAdmin;
    const isCurrentUser = user?.id === id;
    const isDisabled = !canManageRoles || isLoading || isCurrentUser;
    const disabledTooltip = (() => {
        if (isCurrentUser) {
            return intl.formatMessage(messages.currentUserRoleTooltip);
        }

        if (!canManageRoles) {
            return intl.formatMessage(messages.noPermissionTooltip);
        }

        return undefined;
    })();

    const currentValue = roles.find((tempRole) => tempRole.role === role) ?? roles[0];
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

    const onAdminRequestSubmit = () => {
        onSelectRole(UserRole.Admin, onModalClose);
    };

    const onChange = ({ selectedItem }: {selectedItem: EntityAppRole}) => {
        if (selectedItem.role === UserRole.Admin) {
            onAdminRequest();
        } else {
            onSelectRole(selectedItem.role);
        }
    };

    const onAdminRequest = () => {
        setConfirmationModalOpen(true);
    };

    const onModalClose = () => {
        setConfirmationModalOpen(false);
    };

    const roleSelect = (
        <ComboBox
            // @ts-expect-error incorrect typing
            onChange={onChange}
            className={styles.combobox}
            disabled={isDisabled}
            id={id}
            items={selectableRoles}
            warn={isError}
            warnText={intl.formatMessage(sharedMessages.somethingWentWrong)}
            // eslint-disable-next-line no-confusing-arrow
            itemToString={(item) => isLoading ? intl.formatMessage(sharedMessages.loading) : item?.title ?? ''}
            value={currentValue.title}
            titleText=""
            helperText=""
        />
    );

    return (
        <div className={styles.container}>
            {isDisabled && disabledTooltip ? (
                <Tooltip
                    label={disabledTooltip}
                    align="bottom"
                >
                    <span className={styles.tooltipWrapper}>
                        {roleSelect}
                    </span>
                </Tooltip>
            ) : roleSelect}
            <Modal
                open={confirmationModalOpen}
                onRequestClose={onModalClose}
                onRequestSubmit={onAdminRequestSubmit}
                modalHeading={intl.formatMessage(messages.areYouSure)}
                modalLabel={intl.formatMessage(messages.removingEntity)}
                primaryButtonText={intl.formatMessage(sharedMessages.submit)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                secondaryButtonText={intl.formatMessage(sharedMessages.back)}
            />
        </div>
    );
};

const messages = defineMessages({
    removingEntity: {
        id: 'src.Components.Common.UserRole.UserAppRoleSelect.removing',
        defaultMessage: 'Nadawanie praw administratora',
    },
    areYouSure: {
        id: 'src.Components.Common.UserRole.UserAppRoleSelect.areYouSure',
        defaultMessage: 'Jesteś pewien, że chcesz nadać uprawnienia administratora?',
    },
    currentUserRoleTooltip: {
        id: 'src.Components.Common.UserRole.UserAppRoleSelect.currentUserRoleTooltip',
        defaultMessage: 'Nie możesz zmienić własnej roli w tym miejscu. Przekazanie własności parafii wykonaj przez przycisk z prawej strony.',
    },
    noPermissionTooltip: {
        id: 'src.Components.Common.UserRole.UserAppRoleSelect.noPermissionTooltip',
        defaultMessage: 'Nie masz uprawnień do zmiany roli tego użytkownika.',
    },
});

export default UserAppRoleSelect;
